import { useCallback, useState } from 'react';

import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export interface UseCreateMemoryProps extends AssistanceServiceInjectedParams {
	/**
	 * Called with the created memory on success
	 */
	onSuccess?: () => void;
	/**
	 * Called with the error if the mutation fails
	 */
	onError?: (error: ApiError) => void;
}

export interface CreateMemoryCallbacks {
	onSuccess?: () => void;
	onError?: () => void;
}

export interface UseCreateMemoryReturn {
	/**
	 * Function to create a new memory. Accepts optional per-call success/error callbacks
	 * so callers can respond to the outcome without awaiting (useMutation never rejects).
	 */
	onCreateMemory: (content: string, callbacks?: CreateMemoryCallbacks) => void;
	/**
	 * Whether the mutation is in progress
	 */
	isSubmitting: boolean;
	/**
	 * The last error encountered from a network or HTTP failure. Null when there is no error.
	 */
	error: ApiError | null;
}

/**
 * Hook for creating a memory via POST /memories/create.
 *
 * Handles errors via HTTP status codes — the backend returns HTTP 400 for
 * validation failures (e.g. blank content) and HTTP 500 for unknown errors,
 * both surfaced as ApiError via useMutation's onError callback.
 *
 * Accepts optional onSuccess and onError callbacks.
 */
export const useCreateMemory: (
	props: Omit<UseCreateMemoryProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseCreateMemoryReturn = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		onSuccess,
		onError,
	}: UseCreateMemoryProps): UseCreateMemoryReturn => {
		const { assistanceService } = assistanceServiceInjectedParams;
		const [error, setError] = useState<ApiError | null>(null);

		const handleError = useCallback(
			(err: ApiError) => {
				setError(err);
				onError?.(err);
			},
			[onError],
		);

		const { mutate: createMemory, isSubmitting } = useMutation<{ content: string }, void>({
			mutationFn: async (payload: { content: string }) => {
				return assistanceService.createMemory(payload);
			},
			onSuccess: () => {
				setError(null);
				onSuccess?.();
			},
			onError: handleError,
		});

		return {
			onCreateMemory: (content: string, callbacks?: CreateMemoryCallbacks) =>
				void createMemory({ content }, callbacks),
			isSubmitting,
			error,
		};
	},
);
